import { logger } from '@gik/analytics/utils/logger';
import { dotnetApi } from '@gik/core/api/ky/dotnetApi';
import type { RasterAttachImageResult } from '@gik/core/utils/FileUtils';

export interface IBasicPostData {
  message: string;
  backgroundColor?: string;
}

export type createPostResponseBody = {
  id: string;
};

export async function createPost(
  inkindRouteId: string,
  data: IBasicPostData,
  media?: RasterAttachImageResult[]
): Promise<string> {
  let postId: string;
  try {
    const response = await dotnetApi.post(`stream/posts`, {
      json: {
        inkindRouteId,
        ...data,
      },
    });
    // eslint-disable-next-line
    postId = response.headers.get('Location') || ((await response.json()) as any).id;
    if (!postId) {
      throw new Error('Post id not returned in location header or response body');
    } else {
      if (media?.length > 0) {
        await uploadMedia(postId, media);
      }
    }

    await publishPost(postId);
  } catch (e) {
    logger.error('Failed to create post: ', e);
    return null;
  }

  return postId;
}

export async function updatePost(
  postId: string,
  data: IBasicPostData,
  attachmentsToAdd?: RasterAttachImageResult[],
  attachmentsToRemove?: string[]
): Promise<boolean> {
  try {
    const response = await dotnetApi.patch(`stream/posts/${postId}`, {
      json: data,
    });

    if (attachmentsToAdd?.length > 0) {
      await uploadMedia(postId, attachmentsToAdd);
    }

    if (attachmentsToRemove?.length > 0) {
      await deleteMedia(postId, attachmentsToRemove);
    }
    return response.ok;
  } catch (e) {
    logger.error('Failed to update post: ', e);
    return false;
  }
}

export async function deletePost(postId: string, inkindRouteId?: string): Promise<boolean> {
  try {
    const url = inkindRouteId ? `stream/inkinds/${inkindRouteId}/posts/${postId}` : `stream/posts/${postId}`;
    const response = await dotnetApi.delete(url);
    if (response.status != 204) {
      throw new Error(`Unexpected response code when deleting post: ${response.status}`);
    }
    return true;
  } catch (e) {
    logger.error('Failed to hide post: ', e);
    return false;
  }
}

export async function adminDeletePost(postId: string): Promise<boolean> {
  try {
    const response = await dotnetApi.delete(`admin/posts/${postId}`);
    if (response.status != 204) {
      throw new Error(`Unexpected response code when deleting post: ${response.status}`);
    }
    return true;
  } catch (e) {
    logger.error('Failed to hide post: ', e);
    return false;
  }
}

export async function adminUndeletePost(postId: string): Promise<boolean> {
  try {
    const response = await dotnetApi.post(`admin/posts/${postId}/undelete`);
    if (response.status != 204) {
      throw new Error(`Unexpected response code when deleting post: ${response.status}`);
    }
    return true;
  } catch (e) {
    logger.error('Failed to unhide post: ', e);
    return false;
  }
}

export async function uploadMedia(postId: string, media: RasterAttachImageResult[]) {
  for (let i = 0; i < media?.length; i++) {
    try {
      await dotnetApi.post(`stream/posts/${postId}/photos`, {
        json: {
          data: media[i].base64,
        },
      });
    } catch (e) {
      logger.error(e);
    }
  }
}

export async function deleteMedia(postId: string, media: string[]) {
  for (let i = 0; i < media?.length; i++) {
    try {
      await dotnetApi.delete(`stream/posts/${postId}/media/${media[i]}`);
    } catch (e) {
      logger.error(e);
    }
  }
}

export async function publishPost(postId: string) {
  await dotnetApi.post(`stream/posts/${postId}/publish`);
}
